import { useEffect, useMemo, useRef, useState } from "react";
import addressApi from "api/landlord/address";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Input,
  InputSize,
  Modal
} from "shared/components";
import { getErrorMessage } from "utils/error";
import { AlertCircle, User01, Wallet02, XClose } from "untitledui-js-base";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AsyncSelect } from "shared/components/AsyncSelect/AsyncSelect";
import WarningFillIcon from "shared/icons/warningFillIcon.svg?react";
import { ApplicantType } from "shared/types";
import { loadPropertyOptions, loadUnitOptions } from "./api";
import { toast } from "react-toastify";
import { Show } from "shared/components";
import { RoleSelect } from "./components/RoleSelect";
import { cx } from "shared/utils";
import { MagicLink } from "../../../types/landlord/address";

interface InviteApplicantsModalProps {
  opened: boolean;
  onClose: () => void;
  propertyId?: number;
  unitId?: number;
  showOnlyEmails?: boolean;
  magic_link?: MagicLink;
  applicantsNames?: string;
  applicationId?: string | number;
  propertyName?: string;
  unitName?: string;
  queryKey?: unknown[];
}

const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9]{2,12}$/;

export const InviteApplicantsModal = ({
  opened,
  onClose,
  propertyId,
  unitId,
  showOnlyEmails = false,
  applicationId,
  applicantsNames,
  propertyName,
  unitName,
  queryKey
}: InviteApplicantsModalProps) => {
  const emailsInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [emailInput, setEmailInput] = useState<string>("");
  const [roleInput, setRoleInput] = useState<ApplicantType>(
    ApplicantType.Applicant
  );
  const [emailInputError, setEmailInputError] = useState("");

  const [unitError, setUnitError] = useState("");
  const [propertyError, setPropertyError] = useState("");

  const [people, setPeople] = useState<
    { email: string; role: ApplicantType }[]
  >([]);

  const [disableState, setDisabledState] = useState({
    isUnitDisabled: !!unitId,
    isPropertyDisabled: !!propertyId
  });

  const [selectedPropertyId, setSelectedPropertyId] = useState<
    number | undefined
  >(propertyId);
  const [selectedUnitId, setSelectedUnitId] = useState<number | undefined>(
    unitId
  );

  const handleClose = () => {
    // Clear modal state after closing
    setSelectedPropertyId(undefined);
    setSelectedUnitId(undefined);
    setPeople([]);
    setEmailInput("");
    setRoleInput(ApplicantType.Applicant);
    onClose();
  };

  const isValid = () => {
    const allPeople = emailInput
      ? [...people, { email: emailInput, role: roleInput }]
      : [...people];

    if (!allPeople.length) {
      setEmailInputError("At least one email is required");
      return false;
    }

    if (emailInput && !emailRegex.test(emailInput)) {
      setEmailInputError("Please enter a correct email address");
      return false;
    }

    if (showOnlyEmails && !applicationId) {
      toast.error("Application was not provided");
      return false;
    }

    if (!showOnlyEmails && (!selectedUnitId || !selectedPropertyId)) {
      if (!selectedUnitId) setUnitError("Unit is required");
      if (!selectedPropertyId) setPropertyError("Property is required");
      return false;
    }

    return true;
  };

  const { mutate, isPending } = useMutation({
    mutationFn: ({
      application_id,
      unit_id,
      invitees
    }: {
      application_id?: number;
      unit_id?: number;
      invitees: { email: string; role: string }[];
    }) =>
      addressApi.batchInviteApplicants({
        application_id,
        unit_id,
        invitees
      }),
    onSuccess: () => {
      toast.success("Emails have been sent");
      if (queryKey && queryKey.length)
        queryClient.invalidateQueries({
          queryKey
        });
      handleClose();
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
    }
  });

  const inviteApplicant = async () => {
    const allPeople = emailInput
      ? [...people, { email: emailInput, role: roleInput }]
      : [...people];

    if (!isValid()) return;

    const identificator = showOnlyEmails
      ? { application_id: Number(applicationId) || 0 }
      : { unit_id: selectedUnitId };

    mutate({
      ...identificator,
      invitees: allPeople
    });
  };

  const query = useQuery({
    queryKey: ["UNIT_LINK_BY_ID", selectedUnitId, unitId],
    queryFn: () =>
      addressApi.getUnitById(unitId || selectedUnitId!).then((d) => {
        return d;
      }),
    enabled: Boolean(selectedUnitId) || Boolean(unitId)
  });

  const handleCopyLink = async () => {
    try {
      if (!query.data.magic_link?.application_link) {
        toast.error("Link is not available");
        return;
      }

      await navigator.clipboard.writeText(
        query.data.magic_link?.application_link
      );
      toast.info("Link copied");
    } catch (err) {
      toast.error("Error copying link: " + getErrorMessage(err));
    }
  };

  useEffect(() => {
    if (unitId) setSelectedUnitId(unitId);
    if (propertyId) setSelectedPropertyId(propertyId);
  }, [unitId, propertyId]);

  const disabledRoles = useMemo(() => {
    return Object.keys(
      query.data?.verification_template?.settings || {}
    ).filter(
      (key) =>
        query.data?.verification_template?.settings[key]
          ?.enabled_screening_services === false
    );
  }, [query.data?.verification_template?.settings]);

  useEffect(() => {
    if (disabledRoles.length) {
      setPeople((prevPeople) =>
        prevPeople.map((person) => {
          if (disabledRoles.includes(person.role))
            return { ...person, role: ApplicantType.Applicant };
          return person;
        })
      );

      if (disabledRoles.includes(roleInput))
        setRoleInput(ApplicantType.Applicant);
    }
  }, [disabledRoles]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      panelClassName="max-w-[640px] min-h-[640px] max-h-[calc(100dvh-100px)] overflow-y-auto w-full pt-6 flex flex-col"
    >
      <div className="flex items-center justify-between px-6">
        <p className="m-0 text-xl font-bold leading-8">
          {showOnlyEmails ? "Invite applicants to group" : "Invite applicants"}
        </p>
        <IconButton
          variant={IconButtonVariant.BASE}
          size={IconButtonSize.SM}
          borderLess
          className="!justify-end"
          onClick={() => {
            handleClose();
          }}
          Icon={XClose}
        />
      </div>

      <p className="mt-2 pb-6 text-sm text-content-400 border-b border-alpha-40 px-6">
        {showOnlyEmails
          ? applicantsNames
          : "You can invite several people to the unit. They will receive a link to the application form"}
      </p>
      {!showOnlyEmails && (
        <div className="flex items-start py-6 gap-4 border-b border-alpha-40 px-6">
          <div className="w-full">
            <AsyncSelect
              id="property"
              label="Property"
              value={selectedPropertyId || ""}
              searchable={true}
              hideOptional
              disabled={disableState.isPropertyDisabled}
              // @ts-expect-error -- TODO: fix this
              loadOptions={loadPropertyOptions}
              error={propertyError}
              options={
                disableState.isPropertyDisabled
                  ? [
                      {
                        value: propertyId || "",
                        label: propertyName || ""
                      }
                    ]
                  : []
              }
              onChange={(e) => {
                if (!Array.isArray(e)) {
                  setSelectedPropertyId(Number(e.target.value));
                  setPropertyError("");

                  if (!unitId) {
                    setSelectedUnitId(undefined);
                    setDisabledState((prev) => ({
                      ...prev,
                      isUnitDisabled: false
                    }));
                  }
                }
              }}
              additional={{
                page: 1
              }}
            />
          </div>
          <div className="w-full">
            <AsyncSelect
              id="unit"
              label="Unit"
              key={selectedPropertyId}
              searchable={true}
              hideOptional
              disabled={disableState.isUnitDisabled}
              value={selectedUnitId || ""}
              options={
                disableState.isUnitDisabled
                  ? [
                      {
                        value: unitId || "",
                        label: unitName || ""
                      }
                    ]
                  : []
              }
              // @ts-expect-error -- TODO: fix this
              loadOptions={loadUnitOptions}
              error={unitError}
              additional={{ page: 1, propertyId: selectedPropertyId }}
              onChange={(e) => {
                if (!Array.isArray(e)) {
                  setSelectedUnitId(Number(e.target.value));
                  setUnitError("");
                }
              }}
            />
          </div>
        </div>
      )}

      <div className="mt-6 px-6">
        <form
          className={cx(
            "flex items-start justify-center gap-2.5",
            emailInputError ? "" : showOnlyEmails ? "mb-6" : "mb-4"
          )}
        >
          <div className="w-full">
            <Input
              id="email"
              ErrorIcon={AlertCircle}
              label="Add people"
              ref={emailsInputRef}
              placeholder="Add people by email"
              error={emailInputError}
              hideOptional
              value={emailInput}
              className={emailInputError ? "" : "!mb-0"}
              onChange={(e) => {
                if (emailInputError) setEmailInputError("");
                setEmailInput(e.target.value);
              }}
            />
          </div>

          <Show if={emailInput}>
            <RoleSelect
              disabledRoles={disabledRoles as ApplicantType[]}
              size={InputSize.BASE}
              className="mt-6 min-w-[162px] !mb-0 animate-appear"
              id="role"
              isDisabled={!showOnlyEmails && !people.length}
              onChange={(v) => setRoleInput(v)}
              selectedRole={roleInput}
            />
            <Button
              className="mt-6 animate-appear"
              size={ButtonSize.BASE}
              variant={ButtonVariant.PRIMARY}
              disabled={!emailInput}
              type="submit"
              onClick={(e) => {
                e.preventDefault();
                if (people.some((person) => person.email === emailInput)) {
                  toast.error("Email already added");
                  return;
                }

                if (!emailRegex.test(emailInput)) {
                  setEmailInputError("Please enter a correct email address");
                  return;
                }

                setPeople((prev) => [
                  ...prev,
                  { email: emailInput, role: roleInput }
                ]);
                setEmailInput("");
                emailsInputRef.current?.focus();
                setRoleInput(ApplicantType.Applicant);
              }}
            >
              Add
            </Button>
          </Show>
        </form>

        <Show if={!showOnlyEmails}>
          <div
            className={cx(
              "flex items-center gap-1 mb-6",
              emailInputError ? "mt-4" : ""
            )}
          >
            <WarningFillIcon className="text-content-100" />
            <p className="font-medium text-content-400 text-sm">
              First invitee must be applicant
            </p>
          </div>
        </Show>
      </div>

      <div className="flex-grow px-6 border-t  border-alpha-40">
        {people.length ? (
          <>
            {/* TODO: see if needed */}
            <div />
            <ul className="mb-6 max-h-[340px] w-full overflow-y-auto pt-6">
              <p className="text-content-400 text-sm mb-2">Added people</p>
              {people.map((person, index) => (
                <li
                  key={index}
                  className="mb-3 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="bg-primary-20 p-2.5 rounded-full">
                      {person.role === ApplicantType.Applicant ? (
                        <User01
                          size="20"
                          className="stroke-primary-500 stroke-2"
                        />
                      ) : (
                        <Wallet02
                          size="20"
                          className="stroke-primary-500 stroke-2"
                        />
                      )}
                    </div>
                    <p className="text-base font-medium text-content-1000">
                      {person.email}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <RoleSelect
                      disabledRoles={disabledRoles as ApplicantType[]}
                      size={InputSize.SM}
                      className="w-[145px] !mb-0"
                      id={`role-${person.email}`}
                      selectedRole={person.role}
                      isDisabled={!showOnlyEmails && !index}
                      onChange={(v) =>
                        setPeople((prevPeople) =>
                          prevPeople.map((p) =>
                            p.email === person.email ? { ...p, role: v } : p
                          )
                        )
                      }
                    />
                    <IconButton
                      variant={IconButtonVariant.BASE}
                      size={IconButtonSize.SM}
                      Icon={XClose}
                      onClick={() => {
                        setPeople((prevPeople) => {
                          const updatedPeople = prevPeople.filter(
                            (p) => p.email !== person.email
                          );
                          if (updatedPeople.length && !showOnlyEmails)
                            updatedPeople[0].role = ApplicantType.Applicant;
                          return updatedPeople;
                        });
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>

      <div className="py-4 flex justify-between px-6 border-t border-alpha-40">
        <div>
          {!showOnlyEmails && (
            <Button
              size={ButtonSize.SM}
              variant={ButtonVariant.BASE}
              onClick={handleCopyLink}
              disabled={!selectedUnitId && !unitId}
              tooltip={
                !selectedUnitId && !unitId
                  ? "Unit is required to copy the link"
                  : undefined
              }
              tooltipZIndex={50}
            >
              Copy application link
            </Button>
          )}
        </div>
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.PRIMARY}
          onClick={inviteApplicant}
          loading={isPending}
        >
          Send email
        </Button>
      </div>
    </Modal>
  );
};
